import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketingHeader from "@/components/layout/MarketingHeader";
import MarketingFooter from "@/components/layout/MarketingFooter";
import HeraldSignup from "@/components/marketing/HeraldSignup";
import { api } from "@/lib/api";
import { optimizeImage } from "@/lib/img";
import { ArrowUpRight, Search } from "lucide-react";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (cat = active, query = q) => {
    setLoading(true);
    const params = {};
    if (cat && cat !== "All") params.category = cat;
    if (query) params.q = query;
    // Silent fetch with one retry — public read endpoint, no toast on
    // transient network blips. If it fails twice, the page just shows empty.
    const attempt = async () => {
      const { data } = await api.get("/blog", { params });
      return data;
    };
    try {
      const data = await attempt();
      setPosts(data);
    } catch {
      try {
        await new Promise((r) => setTimeout(r, 600));
        const data = await attempt();
        setPosts(data);
      } catch {
        /* silent */
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/blog/categories");
        setCategories(["All", ...data]);
      } catch {
        /* noop */
      }
    })();
    load("All", "");
    // eslint-disable-next-line
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(active, q);
  };

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="min-h-screen" data-testid="blog-page">
      <MarketingHeader />

      {/* Editorial hero */}
      <section
        className="pt-28 lg:pt-36 pb-12 lg:pb-14 section-light"
        style={{ borderBottom: "1px solid var(--lh-border)" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="overline mb-5" style={{ color: "var(--lh-cognac)" }}>
                The LocalizeHER Journal
              </div>
              <h1
                className="font-display-bold text-5xl md:text-7xl lg:text-[5.4rem] leading-[0.95] tracking-tighter"
                data-testid="blog-headline"
              >
                Strategy for women <em>who do.</em>
              </h1>
            </div>
            <p
              className="lg:col-span-4 text-base lg:text-lg font-medium leading-relaxed"
            >
              Marketing, AI, local activation, P&amp;L, advertising, visibility — operator-grade
              writing for women-owned local businesses ready to be category leaders in their cities.
            </p>
          </div>

          {/* Filter / search bar */}
          <div
            className="mt-14 rounded-2xl border p-4 lg:p-5 flex flex-col lg:flex-row gap-4 items-stretch lg:items-center"
            style={{ background: "#FFFFFF", borderColor: "var(--lh-border)" }}
            data-testid="blog-filter-bar"
          >
            <div className="flex-1 overflow-x-auto -mx-1 px-1 no-scrollbar">
              <div className="flex gap-2">
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => {
                      setActive(c);
                      load(c, q);
                    }}
                    className="shrink-0 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors"
                    style={{
                      background:
                        c === active ? "var(--lh-peach)" : "transparent",
                      color:
                        c === active ? "var(--lh-espresso-noir)" : "var(--lh-espresso-noir)",
                      border:
                        c === active
                          ? "1px solid var(--lh-peach)"
                          : "1px solid var(--lh-border)",
                    }}
                    data-testid={`blog-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <form
              onSubmit={onSearch}
              className="flex items-center gap-2 lg:w-80"
              data-testid="blog-search-form"
            >
              <div className="relative flex-1">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: "var(--lh-greige)" }}
                />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search posts…"
                  className="w-full pl-9 pr-3 py-2.5 rounded-full text-sm font-medium outline-none"
                  style={{
                    background: "var(--lh-warm-linen)",
                    color: "var(--lh-espresso-noir)",
                    border: "1px solid var(--lh-border)",
                  }}
                  data-testid="blog-search-input"
                />
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Posts grid */}
      <section className="py-12 lg:py-16 section-light" data-testid="blog-grid-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          {loading ? (
            <div className="text-center py-32 overline" style={{ color: "var(--lh-muted)" }}>
              Loading the journal…
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-32" data-testid="blog-empty">
              <div className="overline mb-3" style={{ color: "var(--lh-muted)" }}>
                Nothing here yet
              </div>
              <p className="text-lg font-medium" style={{ color: "var(--lh-text)" }}>
                Try a different category or clear the search.
              </p>
            </div>
          ) : (
            <>
              {/* Featured */}
              {featured && (
                <Link
                  to={`/blog/${featured.slug}`}
                  className="block group mb-14 lg:mb-20"
                  data-testid="blog-featured"
                >
                  <div className="grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7 overflow-hidden rounded-2xl">
                      {featured.cover_image && (
                        <img
                          src={optimizeImage(featured.cover_image, { w: 1200, name: `localizeher-blog-${featured.slug || "featured"}` })}
                          alt={featured.title}
                          loading="eager"
                          decoding="async"
                          fetchPriority="high"
                          className="w-full h-[320px] lg:h-[520px] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                        />
                      )}
                    </div>
                    <div className="lg:col-span-5">
                      <div
                        className="overline mb-4"
                        style={{ color: "var(--lh-peach)" }}
                      >
                        Featured · {featured.category}
                      </div>
                      <h2 className="font-display-bold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-[var(--lh-warm-ivory)]">
                        {featured.title}
                      </h2>
                      <p
                        className="mt-5 text-base lg:text-lg font-medium leading-relaxed"
                        style={{ color: "rgba(234,216,199,0.82)" }}
                      >
                        {featured.excerpt}
                      </p>
                      <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-[var(--lh-peach)]">
                        Read the piece <ArrowUpRight size={14} />
                      </div>
                      <div
                        className="mt-5 text-xs uppercase tracking-wider"
                        style={{ color: "rgba(234,216,199,0.55)" }}
                      >
                        {featured.read_time} min read · {featured.author}
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              {/* Rest grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {rest.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    className="group block"
                    data-testid={`blog-card-${p.slug}`}
                  >
                    <div className="overflow-hidden rounded-xl mb-5 aspect-[4/3]">
                      {p.cover_image && (
                        <img
                          src={optimizeImage(p.cover_image, { w: 600, name: `localizeher-blog-${p.slug || "post"}` })}
                          alt={p.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                        />
                      )}
                    </div>
                    <div
                      className="overline mb-3"
                      style={{ color: "var(--lh-peach)" }}
                    >
                      {p.category} · {p.read_time} min
                    </div>
                    <h3 className="font-display-bold text-2xl md:text-3xl tracking-tighter leading-[1.05] text-[var(--lh-warm-ivory)] group-hover:text-[var(--lh-peach)] transition-colors">
                      {p.title}
                    </h3>
                    <p
                      className="mt-3 text-sm font-medium leading-relaxed"
                      style={{ color: "rgba(234,216,199,0.78)" }}
                    >
                      {p.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* The HERald newsletter signup */}
      <section
        className="py-12 lg:py-16 section-light"
        style={{ borderTop: "1px solid var(--lh-border)" }}
        data-testid="blog-herald-section"
      >
        <div className="max-w-[760px] mx-auto px-6 md:px-10 lg:px-14">
          <HeraldSignup source="blog-index" />
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
